import { useCart } from "@/contexts/CartContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import StarRating from "@/components/StarRating";
import { Check, Plus, ArrowRight } from "lucide-react";

const BookCard = ({ book }) => {
  const { addBookToCart, isBookInCart, setIsCartOpen } = useCart();

  const handleAddToCart = () => {
    addBookToCart(book);
    setIsCartOpen(true);
  };

  return (
    <Card className={`overflow-hidden hover:shadow-xl transition-all duration-300 border-slate-200 group ${book.featuredBookOfMonth ? 'ring-2 ring-amber-500 shadow-lg' : ''}`} data-testid={`book-card-${book.id}`}>
      <div className="relative overflow-hidden bg-slate-100 flex items-center justify-center" style={{ height: "480px" }}>
        <img 
          src={book.image} 
          alt={book.title}
          className="group-hover:scale-105 transition-transform duration-300"
          style={{ width: "68%", height: "68%", objectFit: "contain" }}
          loading="lazy"
        />
        <Badge className="absolute top-3 right-3 bg-white/90 text-slate-800">{book.category}</Badge>
        {book.featuredBookOfMonth && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
            Featured Book of the Month
          </div>
        )}
      </div>
      <CardHeader>
        <CardTitle className="font-serif text-slate-900">{book.title}</CardTitle>
        <CardDescription>{book.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center">
          <div>
            {book.originalPrice ? (
              <>
                <span className="text-2xl font-bold text-red-600">{book.price}</span>
                <span className="ml-2 text-lg text-slate-400 line-through">{book.originalPrice}</span>
                <div className="text-xs text-red-600 font-semibold">Limited Time Offer!</div>
              </>
            ) : book.limitedTimeOffer ? (
              <>
                <span className="text-2xl font-bold text-amber-700">{book.price}</span>
                <div className="text-xs text-red-600 font-semibold">Limited Time Only!</div>
              </>
            ) : (
              <>
                <span className="text-2xl font-bold text-amber-700">{book.price}</span>
                <div className="text-xs text-slate-500">{book.format}</div>
              </>
            )}
          </div>
          <StarRating rating={book.rating} />
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button 
          variant="outline" 
          className={`flex-1 ${isBookInCart(book.id) ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : ''}`}
          onClick={handleAddToCart}
          disabled={isBookInCart(book.id)}
          data-testid={`add-book-cart-${book.id}`}
        >
          {isBookInCart(book.id) ? (
            <><Check className="mr-1 h-4 w-4" /> In Cart</>
          ) : (
            <><Plus className="mr-1 h-4 w-4" /> Add to Cart</>
          )}
        </Button>
        <a href={book.amazonUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
          <Button className="w-full bg-emerald-900 hover:bg-emerald-800 gold-text-outline" data-testid={`buy-btn-${book.id}`}>Buy Now</Button>
        </a>
      </CardFooter>
    </Card>
  );
};

export default BookCard;
